import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { sameAddress, shortAddr } from "../lib/address";
import { api } from "../lib/api";
import { useNetwork } from "../lib/network";
import { recordOwnership, useOwnership } from "../lib/ownership";
import { utcClock } from "../lib/time";
import { NAV_ITEMS } from "../nav";
import { Brand } from "./Brand";
import { useMaker } from "./MakerGate";

/**
 * Where the trading half of the product lives. It is a *separate* deployment,
 * so the portal only ever links to it. A build-time property of the
 * environment; the deployment descriptor says nothing about frontends.
 */
export const TRADING_APP_URL = import.meta.env.VITE_TRADING_APP_URL ?? "https://129.212.171.39.nip.io";

function SignOutButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="console-signout" aria-label="Sign out" title="Sign out" onClick={onClick}>
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6.5 2.5H3.5v11h3M11 5l3 3-3 3M14 8H6.5" />
      </svg>
    </button>
  );
}

/**
 * The sidebar: the market maker's name, the pages, the trading app link, and
 * the signed-in wallet with Sign out. A session from the admin console holds
 * no wallet: its footer names the AileFX operator and when the session ends.
 */
export function Sidebar() {
  const { session, me, kind, signOut } = useMaker();
  const { chain } = useNetwork();
  const owner = me.executor?.owner ?? null;
  const recordedOwner = sameAddress(owner, session.operator);
  // The recorded owner wallet is only the pending owner while AileFX's token owner holds the executor.
  const watch = kind === "wallet" && recordedOwner;
  const ownership = useOwnership(me.company.id, chain.chainId);
  const unread = ownership === undefined;
  useEffect(() => {
    if (!watch || !unread) return;
    const controller = new AbortController();
    api.executor(chain.chainId, { signal: controller.signal, silent: true }).then(
      (e) => recordOwnership(me.company.id, chain.chainId, e.ownership),
      () => undefined,
    );
    return () => controller.abort();
  }, [watch, unread, chain.chainId, me.company.id]);
  const pending = ownership?.byAileFx === true && sameAddress(ownership.pendingOwner, session.operator);
  const role = pending ? "Operator · pending owner" : recordedOwner ? "Operator · executor owner" : "Operator";
  return (
    <aside className="console-sidebar" aria-label="Navigation">
      <div className="console-sidebar-body">
        <Brand name={me.company.name || session.company.name} sub="AileFX Maker portal" size="sm" mark={null} />
        <nav className="console-nav" aria-label="Pages">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) => (isActive ? "console-nav-item active" : "console-nav-item")}
            >
              <svg className="console-nav-icon" width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={item.icon} />
              </svg>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <span className="console-sidebar-spacer" />

        <a className="console-sidebar-link" href={TRADING_APP_URL} target="_blank" rel="noreferrer noopener">
          ↗ Trading app
        </a>

        {kind === "operator" ? (
          <div className="console-user console-user-console">
            <span className="console-user-avatar" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 2.5l5.5 2v4c0 3.5-2.4 5.8-5.5 7-3.1-1.2-5.5-3.5-5.5-7v-4zM6.5 9l2 2 3.5-4" />
              </svg>
            </span>
            <span className="console-user-meta console-user-meta-console">
              <span className="console-user-line">
                Signed in from the admin console as AileFX operator{" "}
                <span className="console-user-operator" title={session.operator}>
                  {shortAddr(session.operator)}
                </span>
              </span>
              <span className="console-user-caption">until {utcClock(session.expiresAt)} UTC</span>
            </span>
            <SignOutButton onClick={signOut} />
          </div>
        ) : (
          <div className="console-user">
            <span className="console-user-avatar" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2.5 4.5h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1zM2.5 4.5l8-2v2M10.5 9h1" />
              </svg>
            </span>
            <span className="console-user-meta">
              <span className="console-user-address" title={session.operator}>
                {shortAddr(session.operator)}
              </span>
              <span className="console-user-caption">{role}</span>
            </span>
            <SignOutButton onClick={signOut} />
          </div>
        )}
      </div>
    </aside>
  );
}
